/*
 * Copyright (C) 2019 - Potentially Ltd
 *
 * Please see distribution for license.
 */
import { NavigationExtras } from '@angular/router';
import { GlobalSearchPlaylist } from '@app/app/shared/models/global-search/global-search.model';
import { AvailableLanguage, EditorContent, EditorContentType, FavoritePlaylist, ResourceTag } from '..';
import { Moment } from '../../../page-modules/folio/models';
import { FolioBadges } from '../../../page-modules/folio/store/folio.state.model';
import { PageSyndicationDetails } from '../content-store/content-store.model';
import { DataFormSummary } from '../data-form/data-form.model';
import { PageTemplateType } from '../pages/page-section.model';

export interface Page {
  _id: string;
  uri: string;
  formattedUri: string;
  headline: string;
  type: PageType;
  sections: Section[];
  organizationUid?: string;
  frameworkId?: string;
  tagId?: string;
  subheader?: string;
  status: PageStatus;
  thumbnail: string;
  tags: ResourceTag[];
  availableLanguages: AvailableLanguage[];
  syndicationDetails?: PageSyndicationDetails;
  creator: PageCreator;
  adminRights: PageAdminRights[];
  target: PageTargetDetails;
  authorDetails: PageAuthorDetails;
  subHeader: string;
  lastUpdatedOn: string;
  dataForms: DataFormSummary[];
  showOnLeftNav: boolean;
}

export interface SearchPage {
  description: string;
  _id: string;
  thumbnail?: string;
  title: string;
  uri: string;
  type: PageType;
  submittedOn?: string;
  subHeader?: string;
  activeSyndication?: boolean;
}

export interface PageAuthorDetails {
  displayConfig: PageAuthorDisplayConfig;
  name: string;
  uid: string;
  organization: string;
  orgLogoUrl: string;
  userImageUrl: string;
}

export interface PageAuthorDisplayConfig {
  authorName: boolean;
  organizationName: boolean;
  externalAuthor?: {
    image?: string;
    name?: string;
  };
}

export interface PageTargetDetails {
  groups: PageTargetItem[];
  users: PageTargetItem[];
}

export interface PageTargetItem {
  _id: string;
  name: string;
}

export interface PageCreator {
  adminId: string;
  firstName: string;
  lastName: string;
}

export interface PageAdminRights {
  adminId: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  icon?: string;
}

export interface PageList {
  title: string;
  uri: string;
  formattedUri: string;
  type: PageType;
  _id: string;
  subHeader: string;
  contentPackageLinks: string[];
  publisherImage?: string;
  publisherName?: string;
  showOnLeftNav?: boolean;
  published?: boolean;
}

export interface Section {
  uid: string;
  type: SectionTypeValue;
  title: string;
  mappedTitle?: string;
  items?: SectionItem[];
  content?: SectionContent[];
  tagId?: string;
  frameworkId?: string;
  playlists?: FavoritePlaylist[];
  moments?: Moment[];
  badges?: FolioBadges[];
  learnerTags?: FrameworkTrackerSectionTag[];
  totalNumberOfElement?: number;
  loaded?: boolean;
  colour?: string;
}

export interface SectionTitle {
  uid: string;
  type: 'PARAGRAPH';
  content: string;
}

export interface FrameworkTrackerSection {
  uid: string;
  type: SectionTypeValue;
  title: string;
  learnerTags: FrameworkTrackerSectionTag[];
}

export interface FrameworkTrackerSectionTag {
  tag: ResourceTag;
  visible: boolean;
  fromLti: boolean;
  progress: number;
  userScore: number;
  scaleLevel: {
    name: string;
    minPoints: number;
    maxPoints: number;
  };
  nextScaleLevel?: {
    name: string;
    minPoints: number;
    maxPoints: number;
  };
  competencyLevels?: CompetencyLevelStats[];
  selected?: boolean;
}

export type CompetencyLevelStats = {
  uid: string;
  title: string;
  description: string;
  userScore: number;
};

export interface CategoriesSection {
  uid: string;
  type: SectionTypeValue;
  title: string;
  categories: CategoryItem[];
}

export interface CategoryItem {
  frameworkId?: string;
  tagId: string;
  title: string;
  uri: string;
  image: string;
  layout: CategoryItemLayout;
  // front-end specific
  checked?: boolean;
}

export interface SectionItem {
  type: 'PLAYLIST' | 'CARD' | 'LINK';
  playlistUri: string;
  cardUri: string | null;
  composedUri: string;
  key: 'NEW_SKILLS' | 'ALL_SKILLS' | 'UNREAD_MESSAGES' | 'UPCOMING_EVENTS' | 'ANNOUNCEMENTS';
  value: number;
  enabled: boolean;
  // front-end specific
  favorite?: boolean;
  uid?: string;
  _id?: string;
  header?: string;
  headline?: string;
}

export interface SectionContent {
  content: EditorContent[];
  uid: string;
  type: EditorContentType;
}

export interface PageRequest {
  uri?: string;
  headline?: string;
  subHeader?: string;
  type?: string;
  status?: PageStatus;
  template?: PageTemplateType;
}

export interface PageHeadersRequest {
  subHeader: string;
}

export interface SearchExploreRequest {
  term?: string;
  roleFilter: SearchExploreRoleFilter;
  categoryFilter?: {
    categories: Category[];
  };
}

export interface SearchExploreResponse {
  content: {
    content: GlobalSearchPlaylist[];
    totalNumberOfElement: number;
    pageSize: number;
    currentPageNumber: number;
    numberOfPages?: number;
    hasContent?: boolean;
  };
}

export interface Category {
  categoryTagUid: string;
}

/** The request sent on page thumbnail update. */
export interface PageThumbnailUpdateRequest {
  /** The uploaded image name. */
  imageName: string;
  /** The image size in bytes. */
  contentLength: number;
}

export interface PageAuthorInfoUpdateRequest {
  authorName?: boolean;
  organizationName?: boolean;
}

export interface UpdateSyndicationRequest {
  sectionUid: string;
  itemUid: string;
  canSyndicate: boolean;
}

export interface UpdatePageNavigationRequest {
  enable: boolean;
}

/**
 * Defines the request sent when creating a new section.
 */
export interface SectionRequest {
  uid?: string;
  type: SectionTypeValue;
  title: string;
  items?: {
    playlistUid: string;
    cardUid?: string;
  }[];
  content?: DynamicContentRequest[];
  afterSection?: string;
  colour?: string;
}

export interface FrameworkTrackerSectionRequest {
  uid?: string;
  type: SectionTypeValue;
  title: string;
  items?: {
    playlistUid: string;
    cardUid?: string;
  }[];
  content?: DynamicContentRequest[];
  tags: string[];
  afterSection?: string;
}

export interface CategoryItemCreationRequest {
  frameworkId: string;
  tagId: string;
  imageRequest?: {
    image: string;
    contentLength: number;
  };
  layout: CategoryItemLayout;
}

export interface CategoryItemCreationResponse {
  uploadUrl: string;
}

export interface DynamicContentRequest {
  content: string;
  uid: string;
  type: EditorContentType;
  afterSection?: string;
}

export interface LanguageValue {
  languageCode: string;
  dynamicContentRequest?: DynamicContentRequest[];
  dynamicContent?: DynamicContentRequest[];
}

export interface LinkToFeatureData {
  link: string;
  text: string;
  headline: string;
  subLine: string;
  composedUri?: string;
  fontColor: string;
  backgroundColor: string;
  fontSize: number;
  linkStyle: LinkToStyle;
  destination: LinkToDestination;
  thumbnail: string;
  // variable comes from backend
  formattedLink?: string;
  uid?: string;
  // uid of linked page/playlist/card
  itemUid?: string;
  // uids for request body based on destination type
  playlistUid?: string;
  pageUid?: string;
  cardUid?: string;
  // frontend specific
  previewImage?: string;
  file?: File;
  hasAdminRights?: boolean;
  hasTargetedAccess?: boolean;
  isOpenPage?: boolean;
}

export type LinkToDestination = 'WEBLINK' | 'PAGE' | 'PLAYLIST' | 'CARD';
export type LinkToStyle = 'TEXT_COLOR_BACKGROUND' | 'TEXT_IMAGE_BACKGROUND' | 'TEXT_IMAGE';

export interface SectionReorder {
  sections: string[];
}

export interface CardClickData {
  playlistUri: string;
  uri: string;
  formattedUri: string;
  _id: string;
  options?: NavigationExtras;
  playlistUid?: string;
  pageUid?: string;
}

export interface PageLoadData {
  pageNumber: number;
  defaultPageSize: number;
  languageCode: string;
  type?: SectionTypeValue;
}

export type CategoryItemLayout = 'TEXT' | 'SLIM' | 'CARD_B';

export type PageStatus = 'ACTIVE' | 'INACTIVE';

export type PageType =
  | 'HOME_PAGE'
  | 'STANDARD_PAGE'
  | 'DISCOVERY_PAGE'
  | 'CATEGORY_PAGE'
  | 'CONTENT_STORE_PAGE'
  | 'CONTENT_STORE_CATEGORY_PAGE'
  | 'JISC_TABLEAU_DASHBOARD';

export type SectionType = 'FeaturedSection' | 'DynamicSection' | 'ContentSection';

export type SectionTypeValue =
  | 'FEATURED'
  | 'FEATURED_SLIM'
  | 'CONTINUE_PLAYLISTS'
  | 'LATEST_PLAYLISTS'
  | 'USER_MOMENTS'
  | 'ORGANIZATION_MOMENTS'
  | 'FRAMEWORK_TRACKER'
  | 'ACHIEVEMENTS'
  | 'YOUR_PLAYLISTS'
  | 'NEW_PLAYLISTS'
  | 'UPCOMING'
  | 'PAGES'
  | 'DIVIDER'
  | 'HEADLINE_TEXT'
  | 'TEXT_AREA'
  | 'IMAGE_AREA'
  | 'NEW_ELEMENT'
  | 'CATEGORIES'
  | 'PUBLISHER_PLAYLISTS'
  | 'PUBLISHER_PAGES'
  | 'CALLOUT'
  | 'CALLOUT_NEUTRAL'
  | 'TABLEAU_DASHBOARD'
  | 'IMAGE'
  | 'CALLOUT_FEATURED'
  | 'USER_DASHBOARD'
  | 'ANNOUNCEMENTS';

export enum SearchExploreRoleFilter {
  ORGANIZATION = 'ORGANIZATION',
  MEMBERS = 'MEMBERS',
  ORGANIZATION_AND_MEMBERS = 'ORGANIZATION_AND_MEMBERS',
}

export const INITIAL_LOAD_LIMIT = 5;
